import { useEffect, useState } from 'react';
import api from '../services/api';

type ImageRequest = {
  controller: AbortController;
  promise: Promise<string | null>;
  objectUrl: string | null;
  consumers: number;
};

const imageRequests = new Map<string, ImageRequest>();

const acquireImage = (source: string) => {
  let request = imageRequests.get(source);
  if (!request) {
    const controller = new AbortController();
    request = {
      controller,
      objectUrl: null,
      consumers: 0,
      promise: Promise.resolve(null),
    };
    const currentRequest = request;
    imageRequests.set(source, currentRequest);
    currentRequest.promise = api
      .get<Blob>(source, { responseType: 'blob', signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return null;
        currentRequest.objectUrl = URL.createObjectURL(response.data);
        return currentRequest.objectUrl;
      })
      .catch(() => {
        if (imageRequests.get(source) === currentRequest) imageRequests.delete(source);
        return null;
      });
  }

  request.consumers += 1;
  return {
    promise: request.promise,
    release: () => {
      request.consumers -= 1;
      if (request.consumers > 0) return;

      request.controller.abort();
      if (request.objectUrl) URL.revokeObjectURL(request.objectUrl);
      if (imageRequests.get(source) === request) imageRequests.delete(source);
    },
  };
};

export const useAuthenticatedImage = (source?: string | null) => {
  const isExternalUrl = Boolean(source && /^https?:\/\//i.test(source));
  const [loaded, setLoaded] = useState<{ source: string; url: string } | null>(null);

  useEffect(() => {
    if (!source || isExternalUrl) return;

    const { promise, release } = acquireImage(source);
    let active = true;
    promise.then((url) => {
      if (active) setLoaded(url ? { source, url } : null);
    });

    return () => {
      active = false;
      release();
    };
  }, [source, isExternalUrl]);

  if (isExternalUrl) return source ?? null;
  return source && loaded?.source === source && imageRequests.get(source)?.objectUrl === loaded.url
    ? loaded.url
    : null;
};
